import * as React from 'react';
import { codes } from 'currency-codes';
import {
  Select,
  TextArea,
  NumberField,
  DateTime,
  Checkbox,
} from '../components/forms';
import { Link } from 'react-router-dom';
import Price from '../components/forms/Price';
import Page from '../components/Page';
import Section from '../components/Section';
import Box from '../components/Box';
import { ButtonStyle } from '../components/Button';
import { ActionBar, ActionBarStyle } from '../components/ActionBar';
import { Contract } from '../contract';
import { ErrorFlash, SuccessFlash } from '../components/Flash';
import { ProfileContext, ProfileManager } from '../profile';
import { useRequestAsync, getHeaders } from '../api/client';
import { CompanySelect } from '../components/forms/CompaniesSelect';

type Props = {
  contract: Contract;
};

const contractTypes = () => [
  { label: 'Sale Percentage (%)', value: 'sale_percentage' },
  { label: 'Sale Fee/Comission', value: 'sale_fee' },
  { label: 'Monthly Subscription', value: 'monthly_fee' },
];

const update = (context: ProfileManager) =>
  useRequestAsync((data: Contract) => ({
    url: data.id ? `/store/contracts/${data.id}` : '/store/contracts/new',
    method: data.id ? 'PUT' : 'POST',
    body: JSON.stringify(data),
    headers: getHeaders(context),
  }));

const setValue = (obj: Contract, onChange: Function) => (
  field: string,
  newValue: any,
) =>
  onChange(
    Object.assign({}, obj, {
      [field]: newValue,
    }),
  );

export default function EditContract(props: Props) {
  const context = React.useContext(ProfileContext);
  const [contract, setContract] = React.useState(props.contract);

  const [state, submit] = update(context);

  const handleFormSubmit = () =>
    submit({
      ...props.contract,
      ...contract,
    });

  return (
    <Page
      title={contract.company_name || 'A new contract'}
      subtitle="Edit contract"
    >
      <div className="flex justify-center ml-auto mr-auto">
        <Link
          to="/contracts"
          className="f5 link br-pill bg-near-white hover-bg-black-10 ph4 pv3 mh2 b black"
        >
          ← Back to Contracts
        </Link>
      </div>
      {state.error && <ErrorFlash error={state.error} />}
      {state.complete && state.data && (
        <SuccessFlash text="Contract has been saved" />
      )}
      <form onSubmit={handleFormSubmit} id="contract">
        <Section title="Basic details">
          <div className="flex">
            <div className="flex-auto w-50 pr2">
              <CompanySelect
                label="Company"
                field={['company_id']}
                id="company_id"
                defaultValue={contract.company_id}
                onValueChange={setValue(contract, setContract)}
              />
              <TextArea
                label="Additional Notes"
                field={['notes']}
                id="notes"
                defaultValue={contract.notes}
                onValueChange={setValue(contract, setContract)}
              />
            </div>
            <div className="flex-auto w-50 pl2">
              <NumberField
                label="No. of trial days"
                field={['trial_days']}
                id="trial_days"
                defaultValue={contract.trial_days}
                onValueChange={setValue(contract, setContract)}
              />
              <NumberField
                label="Limit of products"
                field={['product_limit']}
                id="product_limit"
                defaultValue={contract.product_limit}
                onValueChange={setValue(contract, setContract)}
              />
              <Checkbox
                label="Active?"
                field={['is_active']}
                id="is_active"
                defaultValue={contract.is_active}
                onValueChange={setValue(contract, setContract)}
              />
              <DateTime
                label="Active From"
                field={['started_at']}
                id="started_at"
                defaultValue={contract.started_at}
                onValueChange={setValue(contract, setContract)}
              />
            </div>
          </div>
        </Section>
        <Section title="Service fee breakdown">
          <div className="flex">
            <div className="flex-auto w-50 pr2">
              <Select
                label="Contract Type"
                field={['type']}
                id="type"
                options={contractTypes()}
                defaultValue={props.contract.type}
                onValueChange={setValue(contract, setContract)}
              />
              <NumberField
                label="Percentage (0-100 %)"
                field={['percentage']}
                id="percentage"
                defaultValue={contract.percentage}
                onValueChange={setValue(contract, setContract)}
              />
            </div>
            <div className="flex-auto w-50 pl2">
              <Price
                label="Comission Price (With decimal point i.e. 8.00)"
                field={['amount']}
                id="amount"
                defaultValue={props.contract.amount}
                onValueChange={setValue(contract, setContract)}
              />
              <Select
                label="Currency"
                field={['amount_currency']}
                id="amount_currency"
                options={codes().map((value: string) => ({
                  value,
                  label: value,
                }))}
                defaultValue={props.contract.amount_currency}
                onValueChange={setValue(contract, setContract)}
              />
            </div>
          </div>
        </Section>
        <ActionBar
          className={ActionBarStyle.alignCenter}
          actions={[
            {
              className: ButtonStyle.Confirm,
              label: 'Save',
              handler: handleFormSubmit,
            },
          ]}
        />
      </form>
    </Page>
  );
}
